<script lang="ts">
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { Icon } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import type { DashPromoGallerySlide } from 'src/components/Dashboards/dash-promo-types';
	import {
		PROMO_FADE_OUT_MS,
		prefersReducedMotion,
	} from 'src/helpers/ui_transitions';

	/**
	 * Promo gallery with seamless infinite loop.
	 *
	 * Architecture (imperative transform — not Svelte-reactive style):
	 *   Track DOM order: [clone(last), ...slides, clone(first)]
	 *   Real slides live at belt indices 1..N
	 *   All motion goes through setTrackX(x, animate). Loop wraps:
	 *     last → animate onto clone(first) → instant snap to real first
	 *     first → animate onto clone(last) → instant snap to real last
	 *   The snap never uses CSS transition, so it cannot be seen.
	 */
	export let slides: DashPromoGallerySlide[] = [];
	export let autoplayMs = 25_000;
	/** Viewport height in px (180 widget / 120 GlobalInlineBanners). */
	export let heightPx = 180;

	const dispatch = createEventDispatcher<{ dismiss: { id: string } }>();

	const FLING_VELOCITY = 0.45;
	const DRAG_LOCK_PX = 6;
	const TRANSITION_MS = 400;
	const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
	const CHROME_SELECTOR =
		'.promo-gallery-widget__dismiss, .promo-gallery-widget__nav, .promo-gallery-widget__dot';

	let viewportWidth = 0;
	let viewportEl: HTMLDivElement | null = null;
	let trackEl: HTMLDivElement | null = null;

	/** Index into the official slides array (0..N-1). */
	let logicalIndex = 0;
	/** Pixel translateX of the track (source of truth — applied imperatively). */
	let trackX = 0;

	let isAnimating = false;
	let isDragging = false;
	let dragLocked = false;
	let suppressClick = false;
	let dragStartX: number | null = null;
	let dragOriginTrackX = 0;
	let velocityX = 0;
	let lastMoveX = 0;
	let lastMoveT = 0;
	let activePointerId: number | null = null;
	let lastSlideCount = -1;
	let lastViewportWidth = 0;
	let navHovered = false;
	let autoplayTimer: ReturnType<typeof setTimeout> | null = null;
	let transitionHandler: ((e: TransitionEvent) => void) | null = null;
	/** Fade active slide before calling onDismiss / parent removal. */
	let dismissFading = false;
	let isDestroyed = false;

	$: slideCount = slides.length;
	$: activeSlide = slides[logicalIndex];
	$: showDismiss = Boolean(activeSlide?.dismissable !== false && activeSlide?.onDismiss);
	$: beltSlides =
		slideCount > 1 ? [slides[slideCount - 1], ...slides, slides[0]] : slides;
	$: navFaded = !navHovered && !isDragging && !isAnimating;

	/** Belt index of the real slide at `logicalIndex` (1-based when clones exist). */
	const realBeltIndex = (index: number) => (slideCount > 1 ? index + 1 : 0);

	const xForBeltIndex = (beltIndex: number) => -beltIndex * viewportWidth;

	const clearAutoplay = () => {
		if (autoplayTimer) {
			clearTimeout(autoplayTimer);
			autoplayTimer = null;
		}
	};

	const startAutoplay = () => {
		clearAutoplay();
		if (slideCount <= 1 || isDragging || isAnimating || viewportWidth <= 0) return;
		autoplayTimer = setTimeout(() => {
			autoplayTimer = null;
			if (isDragging || isAnimating) return;
			void goNext();
		}, autoplayMs);
	};

	const pauseAutoplay = () => {
		clearAutoplay();
	};

	/**
	 * Sole writer of track transform. Snaps never pass animate=true.
	 */
	const setTrackX = (x: number, animate: boolean) => {
		trackX = x;
		if (!trackEl) return;
		trackEl.style.transition = animate
			? `transform ${TRANSITION_MS}ms ${EASING}`
			: 'none';
		trackEl.style.transform = `translate3d(${x}px, 0, 0)`;
	};

	/**
	 * Instant fake replace. Freezes any in-flight transition at its current
	 * computed matrix first, then jumps — so clone→real can never animate.
	 */
	const snapTrackX = (x: number) => {
		trackX = x;
		if (!trackEl) return;

		const computed = getComputedStyle(trackEl).transform;
		trackEl.style.transition = 'none';
		if (computed && computed !== 'none') {
			trackEl.style.transform = computed;
		}
		void trackEl.offsetHeight;

		trackEl.style.transform = `translate3d(${x}px, 0, 0)`;
		void trackEl.offsetHeight;
	};

	const waitForTransformEnd = (timeoutMs = TRANSITION_MS + 80): Promise<void> => {
		if (!trackEl) return Promise.resolve();
		return new Promise((resolve) => {
			let done = false;
			const finish = () => {
				if (done) return;
				done = true;
				if (transitionHandler && trackEl) {
					trackEl.removeEventListener('transitionend', transitionHandler);
				}
				transitionHandler = null;
				clearTimeout(timer);
				resolve();
			};
			transitionHandler = (event: TransitionEvent) => {
				if (event.target !== trackEl) return;
				if (event.propertyName !== 'transform') return;
				finish();
			};
			trackEl.addEventListener('transitionend', transitionHandler);
			const timer = setTimeout(finish, timeoutMs);
		});
	};

	/**
	 * Animate to a pixel X, then optionally run an invisible snap (loop wrap).
	 */
	const animateTrackTo = async (x: number, afterSnapX?: number) => {
		if (!trackEl || viewportWidth <= 0) return;

		if (Math.abs(x - trackX) < 0.5) {
			if (afterSnapX !== undefined) snapTrackX(afterSnapX);
			if (!isDragging) startAutoplay();
			return;
		}

		isAnimating = true;
		pauseAutoplay();
		setTrackX(x, true);
		await waitForTransformEnd();

		if (afterSnapX !== undefined) {
			snapTrackX(afterSnapX);
		} else {
			// Kill transition so the settled frame cannot carry into the next move.
			setTrackX(trackX, false);
			if (trackEl) void trackEl.offsetHeight;
		}

		isAnimating = false;
		if (!isDragging) startAutoplay();
	};

	const syncToLogicalIndex = (animate: boolean) => {
		const x = xForBeltIndex(realBeltIndex(logicalIndex));
		if (animate) {
			void animateTrackTo(x);
		} else {
			snapTrackX(x);
		}
	};

	const resetToStart = () => {
		logicalIndex = Math.min(logicalIndex, Math.max(slideCount - 1, 0));
		isAnimating = false;
		isDragging = false;
		dragLocked = false;
		dragStartX = null;
		snapTrackX(xForBeltIndex(realBeltIndex(logicalIndex)));
	};

	const goNext = async () => {
		if (slideCount <= 1 || isAnimating || isDragging || viewportWidth <= 0) return;

		const nextLogical = (logicalIndex + 1) % slideCount;
		const wrapping = logicalIndex === slideCount - 1;

		logicalIndex = nextLogical;

		if (wrapping) {
			// Only ever animate onto the trailing clone — never toward real first.
			const cloneFirstX = xForBeltIndex(slideCount + 1);
			const realFirstX = xForBeltIndex(1);
			await animateTrackTo(cloneFirstX, realFirstX);
			return;
		}

		await animateTrackTo(xForBeltIndex(realBeltIndex(nextLogical)));
	};

	const goPrev = async () => {
		if (slideCount <= 1 || isAnimating || isDragging || viewportWidth <= 0) return;

		const prevLogical = (logicalIndex - 1 + slideCount) % slideCount;
		const wrapping = logicalIndex === 0;

		logicalIndex = prevLogical;

		if (wrapping) {
			const cloneLastX = xForBeltIndex(0);
			const realLastX = xForBeltIndex(slideCount);
			await animateTrackTo(cloneLastX, realLastX);
			return;
		}

		await animateTrackTo(xForBeltIndex(realBeltIndex(prevLogical)));
	};

	const goTo = async (index: number) => {
		if (slideCount <= 1 || isAnimating || isDragging || index === logicalIndex) return;
		if (viewportWidth <= 0) return;

		const forwardDist = (index - logicalIndex + slideCount) % slideCount;
		const backwardDist = (logicalIndex - index + slideCount) % slideCount;

		if (forwardDist <= backwardDist) {
			// Walk forward (may wrap once via clone).
			if (logicalIndex + forwardDist >= slideCount) {
				logicalIndex = index;
				await animateTrackTo(xForBeltIndex(slideCount + 1), xForBeltIndex(1));
			} else {
				logicalIndex = index;
				await animateTrackTo(xForBeltIndex(realBeltIndex(index)));
			}
		} else {
			if (logicalIndex - backwardDist < 0) {
				logicalIndex = index;
				await animateTrackTo(xForBeltIndex(0), xForBeltIndex(slideCount));
			} else {
				logicalIndex = index;
				await animateTrackTo(xForBeltIndex(realBeltIndex(index)));
			}
		}
	};

	const handleDismiss = async () => {
		const slide = slides[logicalIndex];
		if (!slide?.onDismiss || dismissFading) return;

		pauseAutoplay();
		dismissFading = true;
		const fadeMs = prefersReducedMotion() ? 0 : PROMO_FADE_OUT_MS;
		await new Promise((r) => setTimeout(r, fadeMs));
		if (isDestroyed) return;

		try {
			await slide.onDismiss();
			if (!isDestroyed) {
				dispatch('dismiss', { id: slide.id });
			}
		} finally {
			if (!isDestroyed) {
				dismissFading = false;
				startAutoplay();
			}
		}
	};

	const releasePointer = (event?: PointerEvent) => {
		if (
			event &&
			activePointerId !== null &&
			viewportEl?.hasPointerCapture?.(activePointerId)
		) {
			try {
				viewportEl.releasePointerCapture(activePointerId);
			} catch {
				/* already released */
			}
		}
		activePointerId = null;
	};

	const removeDragWindowListeners = () => {
		window.removeEventListener('pointermove', onWindowPointerMove);
		window.removeEventListener('pointerup', onWindowPointerUp);
		window.removeEventListener('pointercancel', onWindowPointerCancel);
	};

	const isPointerOutsideViewport = (clientX: number, clientY: number) => {
		if (!viewportEl) return false;
		const rect = viewportEl.getBoundingClientRect();
		// 1px inset so leaving across the border reliably counts as outside
		return (
			clientX <= rect.left + 1 ||
			clientX >= rect.right - 1 ||
			clientY <= rect.top + 1 ||
			clientY >= rect.bottom - 1
		);
	};

	/**
	 * Snap to whichever belt slot occupies more than half the viewport,
	 * including seamless wrap via clones when that slot is a clone.
	 */
	const settleToMajorityVisible = async () => {
		if (slideCount <= 1 || viewportWidth <= 0) {
			await animateTrackTo(xForBeltIndex(realBeltIndex(logicalIndex)));
			return;
		}

		const maxBelt = slideCount + 1;
		let targetBelt = Math.round(-trackX / viewportWidth);
		targetBelt = Math.max(0, Math.min(maxBelt, targetBelt));

		if (targetBelt === 0) {
			logicalIndex = slideCount - 1;
			await animateTrackTo(xForBeltIndex(0), xForBeltIndex(slideCount));
			return;
		}
		if (targetBelt >= slideCount + 1) {
			logicalIndex = 0;
			await animateTrackTo(xForBeltIndex(slideCount + 1), xForBeltIndex(1));
			return;
		}

		logicalIndex = targetBelt - 1;
		await animateTrackTo(xForBeltIndex(targetBelt));
	};

	const finishDrag = async (
		event: PointerEvent | null,
		reason: 'release' | 'leave' | 'cancel',
	) => {
		if (!isDragging) return;
		if (
			event &&
			activePointerId !== null &&
			event.pointerId !== activePointerId
		) {
			return;
		}

		const velocity = velocityX;
		const hadDragLock = dragLocked;

		isDragging = false;
		dragLocked = false;
		dragStartX = null;
		removeDragWindowListeners();
		if (event) releasePointer(event);
		else releasePointer();

		if (!hadDragLock) {
			await animateTrackTo(xForBeltIndex(realBeltIndex(logicalIndex)));
			return;
		}

		// Leaving the frame (or a normal release after a real drag): settle to
		// whichever banner owns more of the viewport. Strong flings still win.
		const flingNext = velocity <= -FLING_VELOCITY;
		const flingPrev = velocity >= FLING_VELOCITY;

		if (reason !== 'leave' && flingNext) {
			await goNext();
			return;
		}
		if (reason !== 'leave' && flingPrev) {
			await goPrev();
			return;
		}

		await settleToMajorityVisible();
	};

	const onWindowPointerMove = (event: PointerEvent) => {
		if (!isDragging || dragStartX === null) return;
		if (activePointerId !== null && event.pointerId !== activePointerId) return;

		const now = performance.now();
		const dt = now - lastMoveT;
		if (dt > 0) {
			velocityX = (event.clientX - lastMoveX) / dt;
		}
		lastMoveX = event.clientX;
		lastMoveT = now;

		const delta = event.clientX - dragStartX;
		if (!dragLocked && Math.abs(delta) >= DRAG_LOCK_PX) {
			dragLocked = true;
			suppressClick = true;
		}

		if (!dragLocked) return;

		event.preventDefault();
		setTrackX(dragOriginTrackX + delta, false);

		if (isPointerOutsideViewport(event.clientX, event.clientY)) {
			void finishDrag(event, 'leave');
		}
	};

	const onWindowPointerUp = (event: PointerEvent) => {
		void finishDrag(event, 'release');
	};

	const onWindowPointerCancel = (event: PointerEvent) => {
		void finishDrag(event, 'cancel');
	};

	const onPointerDown = (event: PointerEvent) => {
		if (slideCount <= 1 || isAnimating) return;
		if (event.button !== undefined && event.button !== 0) return;
		const target = event.target as HTMLElement | null;
		if (target?.closest?.(CHROME_SELECTOR)) return;

		event.stopPropagation();
		event.preventDefault();

		isDragging = true;
		dragLocked = false;
		suppressClick = false;
		dragStartX = event.clientX;
		dragOriginTrackX = trackX;
		velocityX = 0;
		lastMoveX = event.clientX;
		lastMoveT = performance.now();
		activePointerId = event.pointerId;
		pauseAutoplay();
		setTrackX(trackX, false);

		// Window listeners keep receiving moves after the cursor leaves the widget.
		window.addEventListener('pointermove', onWindowPointerMove, { passive: false });
		window.addEventListener('pointerup', onWindowPointerUp);
		window.addEventListener('pointercancel', onWindowPointerCancel);
		viewportEl?.setPointerCapture?.(event.pointerId);
	};

	const onClickCapture = (event: MouseEvent) => {
		if (!suppressClick) return;
		suppressClick = false;
		event.preventDefault();
		event.stopPropagation();
	};

	$: if (slideCount !== lastSlideCount) {
		lastSlideCount = slideCount;
		resetToStart();
		startAutoplay();
	}

	$: if (viewportWidth > 0 && viewportWidth !== lastViewportWidth) {
		lastViewportWidth = viewportWidth;
		// Re-anchor without animation after layout changes.
		snapTrackX(xForBeltIndex(realBeltIndex(logicalIndex)));
	}

	onMount(() => {
		resetToStart();
		startAutoplay();
	});

	onDestroy(() => {
		isDestroyed = true;
		clearAutoplay();
		removeDragWindowListeners();
		if (transitionHandler && trackEl) {
			trackEl.removeEventListener('transitionend', transitionHandler);
		}
	});
</script>

{#if slideCount > 0}
	<div
		class="promo-gallery-widget"
		class:promo-gallery-widget--compact={heightPx <= 120}
		class:promo-gallery-widget--dismissing={dismissFading}
		style="--promo-gallery-height: {heightPx}px;"
	>
		<div
			class="promo-gallery-widget__viewport"
			class:promo-gallery-widget__viewport--dragging={dragLocked}
			role="region"
			aria-roledescription="carousel"
			aria-label="Promotions"
			bind:this={viewportEl}
			bind:clientWidth={viewportWidth}
			on:pointerdown={onPointerDown}
			on:click|capture={onClickCapture}
			on:mousedown|stopPropagation
			on:touchstart|stopPropagation
		>
			{#if viewportWidth > 0}
				<div class="promo-gallery-widget__track" bind:this={trackEl}>
					{#each beltSlides as slide, i (
						i === 0 && slideCount > 1
							? `${slide.id}-leading-clone`
							: i === slideCount + 1
								? `${slide.id}-trailing-clone`
								: slide.id
					)}
						<div
							class="promo-gallery-widget__slide"
							class:promo-gallery-widget__slide--dismissing={dismissFading &&
								(slideCount <= 1 || i === realBeltIndex(logicalIndex))}
							style="width: {viewportWidth}px; flex: 0 0 {viewportWidth}px;"
							aria-hidden={slideCount > 1
								? i !== realBeltIndex(logicalIndex)
								: false}
						>
							<svelte:component this={slide.component} {...slide.props} />
						</div>
					{/each}
				</div>
			{/if}

			{#if showDismiss}
				<button
					type="button"
					class="promo-gallery-widget__dismiss reset-button-styles-base"
					aria-label={$format('button_dismiss', { default: 'Dismiss' })}
					on:click={handleDismiss}
				>
					<Icon icon="fal fa-times" size="s" />
				</button>
			{/if}

			{#if slideCount > 1}
				<div
					class="promo-gallery-widget__nav"
					class:promo-gallery-widget__nav--faded={navFaded}
					role="tablist"
					aria-label="Promotion slides"
					on:pointerenter={() => (navHovered = true)}
					on:pointerleave={() => (navHovered = false)}
				>
					{#each slides as slide, i (slide.id)}
						<button
							type="button"
							class="promo-gallery-widget__dot"
							class:promo-gallery-widget__dot--active={i === logicalIndex}
							role="tab"
							aria-selected={i === logicalIndex}
							aria-label={`Slide ${i + 1} of ${slideCount}`}
							on:click={() => goTo(i)}
						></button>
					{/each}
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	.promo-gallery-widget {
		width: 100%;
		height: var(--promo-gallery-height, 180px);
		max-height: var(--promo-gallery-height, 180px);
		border-radius: 16px;
		border: 1px solid rgba(255, 255, 255, 0.12);
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
		background: rgba(255, 255, 255, 0.03);
		box-sizing: border-box;
		overflow: hidden;
	}

	.promo-gallery-widget__viewport {
		overflow: hidden;
		touch-action: none;
		cursor: grab;
		width: 100%;
		height: 100%;
		max-height: var(--promo-gallery-height, 180px);
		position: relative;
		user-select: none;
		-webkit-user-select: none;
	}

	.promo-gallery-widget__viewport:active,
	.promo-gallery-widget__viewport--dragging {
		cursor: grabbing;
	}

	.promo-gallery-widget__slide :global(img) {
		-webkit-user-drag: none;
		user-drag: none;
		pointer-events: none;
	}

	.promo-gallery-widget__track {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: stretch;
		height: 100%;
		will-change: transform;
		/* transition is set imperatively — never via reactive class toggles */
	}

	.promo-gallery-widget__slide {
		box-sizing: border-box;
		position: relative;
		overflow: hidden;
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		height: 100%;
		max-height: var(--promo-gallery-height, 180px);
		opacity: 1;
		transition: opacity 180ms ease-out;
	}

	.promo-gallery-widget__slide--dismissing {
		opacity: 0;
		pointer-events: none;
	}

	.promo-gallery-widget--dismissing .promo-gallery-widget__dismiss,
	.promo-gallery-widget--dismissing .promo-gallery-widget__nav {
		opacity: 0;
		pointer-events: none;
		transition: opacity 180ms ease-out;
	}

	@media (prefers-reduced-motion: reduce) {
		.promo-gallery-widget__slide,
		.promo-gallery-widget--dismissing .promo-gallery-widget__dismiss,
		.promo-gallery-widget--dismissing .promo-gallery-widget__nav {
			transition: none;
		}
	}

	.promo-gallery-widget__slide > :global(*) {
		flex: 1 1 auto;
		width: 100%;
		height: 100%;
		min-height: 0;
		max-height: var(--promo-gallery-height, 180px);
	}

	.promo-gallery-widget__dismiss {
		position: absolute;
		top: 10px;
		right: 10px;
		z-index: 7;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		border-radius: 6px;
		color: #ffffff;
		cursor: pointer;
		opacity: 0.92;
		background: rgba(0, 0, 0, 0.22);
		backdrop-filter: blur(4px);
	}

	.promo-gallery-widget__dismiss:hover {
		opacity: 1;
		background: rgba(0, 0, 0, 0.35);
	}

	.promo-gallery-widget--compact {
		border-radius: 12px;
	}

	.promo-gallery-widget--compact .promo-gallery-widget__dismiss {
		top: 6px;
		right: 6px;
		width: 22px;
		height: 22px;
		border-radius: 5px;
	}

	.promo-gallery-widget--compact .promo-gallery-widget__nav {
		bottom: 6px;
		gap: 5px;
		padding: 4px 8px;
	}

	.promo-gallery-widget--compact .promo-gallery-widget__dot {
		width: 6px;
		height: 6px;
	}

	.promo-gallery-widget__nav {
		position: absolute;
		left: 50%;
		bottom: 12px;
		transform: translateX(-50%);
		z-index: 6;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 7px;
		padding: 7px 12px;
		border-radius: 999px;
		background: rgba(0, 0, 0, 0.28);
		backdrop-filter: blur(8px);
		-webkit-backdrop-filter: blur(8px);
		border: 1px solid rgba(255, 255, 255, 0.12);
		opacity: 1;
		transition: opacity 0.25s ease;
		pointer-events: auto;
	}

	.promo-gallery-widget__nav--faded {
		opacity: 0.28;
	}

	.promo-gallery-widget__nav:hover,
	.promo-gallery-widget__nav:focus-within {
		opacity: 1;
	}

	.promo-gallery-widget__dot {
		width: 7px;
		height: 7px;
		padding: 0;
		border: none;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.35);
		cursor: pointer;
		transition:
			background-color 0.2s ease,
			transform 0.2s ease;
	}

	.promo-gallery-widget__dot--active {
		background: rgba(255, 255, 255, 0.95);
		transform: scale(1.2);
	}

	.promo-gallery-widget__dot:hover {
		background: rgba(255, 255, 255, 0.7);
	}

	.promo-gallery-widget__slide :global(.verivox-banner),
	.promo-gallery-widget__slide :global(.verivox-offers),
	.promo-gallery-widget__slide :global(.generic-promo-banner),
	.promo-gallery-widget__slide :global(.campaign-promo) {
		border-radius: 0;
	}
</style>
